// src/components/messenger/ChatHeader.tsx

import React from 'react';
import {
  ChatHeaderProfile,
  HeaderIconStrokeButton,
  ConversationListHeader2,
  MobileBackButton,
  HideOnMobileText,
} from '../../styles/messenger/layout.style';
import { ProfileInitial2, Avatar2, ConversationName } from '../../styles/messenger/messenger.style';
import { CalendarPlus, ChevronLeft } from 'lucide-react';
import { Connection } from '../../hooks/useConnections';
import { SecondaryButton } from '../../styles/search/search.style';
import { PlusCircleFill } from 'react-bootstrap-icons';
import { UserDetails } from '../../types/schemas/abc';
import * as Popover from '@radix-ui/react-popover';
import { ScheduleView } from './ScheduleView';
import {
    PopoverContent,
    PopoverArrow,
} from '../../styles/messenger/layout.style';
import { getTimezoneOffset } from '../../utils/availability';
import { getDetectedTimezone, shortTzName } from '../../utils/timezones';

import { getInitial as getInitials } from '../../utils/name';

type UserProfile = Connection['parent'] | Connection['tutor'];

interface ChatHeaderProps {
  otherUser: UserProfile | null;
  currentUser: UserDetails | null;
  onProfileClick: () => void;
  onAddLessonClick: () => void;
  onReturn: () => void;
  isAdminConnection?: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  otherUser,
  currentUser,
  onProfileClick,
  onAddLessonClick,
  onReturn,
  isAdminConnection = false,
}) => {
  const isParent = currentUser?.role === 'parent';
  const tutor = currentUser?.role === 'tutor' ? currentUser : otherUser;
  const parent = currentUser?.role === 'parent' ? currentUser : otherUser;

  const firstName = otherUser?.name ? otherUser.name.split(' ')[0] : '';

  const timezoneBanner = React.useMemo(() => {
    if (!currentUser?.timezone || !otherUser?.timezone) return null;
    if (currentUser.timezone === otherUser.timezone) return null;
    const offsetMin = getTimezoneOffset(otherUser.timezone, currentUser.timezone);
    if (offsetMin === 0) return null;
    const hrs = Math.abs(Math.round(offsetMin / 60));
    const label = hrs === 1 ? 'hour' : 'hours';
    const direction = offsetMin > 0 ? 'ahead of' : 'behind';
    const homeName = shortTzName(currentUser.timezone);
    const browserTz = getDetectedTimezone();
    const isTraveling = browserTz !== currentUser.timezone;
    let msg = `${firstName} is ${hrs} ${label} ${direction} ${homeName} time`;
    if (isTraveling) msg += ` — you're currently in ${shortTzName(browserTz)}`;
    return msg;
  }, [currentUser?.timezone, otherUser?.timezone, firstName]);

  return (
    <>
    <ConversationListHeader2 style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {otherUser ? (
        <>
          {/* --- Left Element (Profile Button & Name) --- */}
          <ChatHeaderProfile style={{ justifySelf: 'start', zIndex: 2 }}> 
            <MobileBackButton onClick={onReturn}>
                <ChevronLeft size={25}/>
            </MobileBackButton>

            {isAdminConnection ? (
              // Admin connection: show name without clickable profile button
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 1rem 0.25rem 0.25rem' }}>
                {otherUser.role === 'tutor' ? (
                  <Avatar2
                    src={otherUser.profilePhotoUrl || '/default-avatar.png'}
                    alt={otherUser.name}
                  />
                ) : (
                  <ProfileInitial2 style={{ backgroundColor: otherUser.profileColor || '#ccc', width: '38px', height: '38px' }}>
                    {getInitials(otherUser.name)}
                  </ProfileInitial2>
                )}
                <ConversationName>{firstName}</ConversationName>
              </div>
            ) : (
              <SecondaryButton css={{padding: '0.25rem', paddingRight: '1rem'}} onClick={onProfileClick} normal={false}>
                {otherUser.role === 'tutor' ? (
                  <Avatar2
                    src={otherUser.profilePhotoUrl || '/default-avatar.png'}
                    alt={otherUser.name}
                  />
                ) : (
                  <ProfileInitial2 style={{ backgroundColor: otherUser.profileColor || '#ccc', width: '38px', height: '38px' }}>
                    {getInitials(otherUser.name)}
                  </ProfileInitial2>
                )}
                <ConversationName>{firstName}</ConversationName> 
              </SecondaryButton>
            )}
          </ChatHeaderProfile>

          {/* --- Center Element (Calendar) --- */}
          {!isAdminConnection && (
            <div style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              zIndex: 1
            }}>
              <Popover.Root>
                  <Popover.Trigger asChild>
                    <HeaderIconStrokeButton>
                      <CalendarPlus size={22} />
                    </HeaderIconStrokeButton>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <PopoverContent sideOffset={5}>
                      {tutor && parent && currentUser && <ScheduleView tutor={tutor} parent={parent} currentUserRole={currentUser.role}/>}
                      <PopoverArrow />
                    </PopoverContent>
                  </Popover.Portal>
              </Popover.Root>
            </div>
          )}
    
          {/* --- Right Element (Add Lesson) --- */}
          {!isAdminConnection && (
            <div style={{ justifySelf: 'end', zIndex: 2, display: 'flex' }}>
              {isParent && (
                <button
                  onClick={onAddLessonClick}
                  style={{
                    all: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    border: '1px solid var(--colors-sand11)',
                    color: 'var(--colors-sand11)',
                    background: 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget;
                    el.style.background = 'var(--colors-blueHover, #eff6ff)';
                    el.style.color = 'var(--colors-blue11)';
                    el.style.borderColor = 'var(--colors-blue7)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget;
                    el.style.background = 'transparent';
                    el.style.color = 'var(--colors-sand11)';
                    el.style.borderColor = 'var(--colors-sand11)';
                  }}
                >
                  <PlusCircleFill size={14} />
                  <span>
                    <HideOnMobileText>
                      Book&nbsp;
                    </HideOnMobileText>
                    Lesson
                  </span>
                </button>
              )}
            </div>
          )}
        </>
      ) : null}
    </ConversationListHeader2>
    {timezoneBanner && (
      <div style={{
        background: '#ecfdf5',
        borderBottom: '1px solid #a7f3d0',
        padding: '0.35rem 1rem',
        fontSize: '0.8rem',
        color: '#065f46',
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
      }}>
        <span style={{ fontSize: '0.85rem' }}>&#x1f310;</span>
        {timezoneBanner}
      </div>
    )}
    </>
  );
};